import {
	get,
	post,
	put,
	toJson,
	checkStatusVerbose,
} from "../../../gulp/scripts/typescript/helpers/fetch";
import { ICheckoutInitResponse } from "../../../../Site.Checkout/src/src/models/ICheckoutInitResponse.model";
import { ISaveDeliveryAddressResponse } from "../../../../Site.Checkout/src/src/models/ISaveDeliveryAddressResponse.model";
import { IErrorResponse } from "../../../../Site.Checkout/src/src/models/IErrorResponse.model";
import IDeliveryOptionsSaveResponse from "../../../../Site.Checkout/src/src/models/IDeliveryOptionsSaveResponse.model";
import IInitialisePaymentResponse from "../../../../Site.Checkout/src/src/models/IInitialisePaymentResponse";
import ICompleteOrderResponse from "../../../../Site.Checkout/src/src/models/ICompleteOrderResponse";

const checkoutApi = "/checkout/api/v2";

// Express checkout has its own controller/routes (ExpressCheckoutController), separate from the
// full checkout's CheckoutController, so nothing here can ever affect the full checkout's
// initialise-payment/complete-order calls.
interface IExpressCheckoutInitialisePaymentRequest {
	selectedPaymentMethodName: string;
	email: string;
	paymentAmount: number;
}

interface IExpressCheckoutCompleteOrderRequest {
	email: string;
	selectedPaymentMethodName: string;
	stripePaymentMethodId?: string;
	payPalOrderId?: string;
	payPalPayerId?: string;
}

/**
 * The basket page has no checkout session by default. init() can be called any time and has no
 * dependency on an email. The buyer's email (only revealed partway through the wallet's own UI -
 * e.g. Stripe's billingDetails on confirm, PayPal's payer info on approval) is passed straight into
 * initialisePayment()/completeOrder() instead of a separate guest-auth call - the backend sets up
 * the guest session (or attaches the order to an existing account by email match) as part of those
 * requests. See ExpressGuestAuthenticationService.
 */
class ExpressCheckoutService {
	async init(): Promise<ICheckoutInitResponse> {
		return get(`${checkoutApi}/checkout/init`)
			.then(checkStatusVerbose)
			.then(toJson);
	}

	/**
	 * Sets a coarse (postcode/city/country only) delivery address for express-checkout negotiation,
	 * so delivery options can be priced against it before the buyer commits to a payment - targets
	 * the express-specific endpoint/model, not the full-address one used by the manual checkout flow
	 * (which requires FirstName/Surname/Address1 etc. and would reject this shape). See
	 * ExpressCheckoutCoarseAddressRequestModel on the backend.
	 */
	async saveCoarseAddress(address: {
		city: string;
		postcode: string;
		countryCode: string;
		payPalOrderId?: string;
		isStripeExpressCheckout?: boolean;
	}): Promise<ISaveDeliveryAddressResponse | IErrorResponse> {
		return put(`${checkoutApi}/express-checkout/delivery-address/coarse`, address).then(
			(response) => {
				if (
					response.status === 200 ||
					response.status === 400 ||
					response.status === 409
				) {
					return Promise.resolve(response.json());
				} else {
					return Promise.reject(
						new Error(`${response.status} ${response.statusText}`.trim()),
					);
				}
			},
		);
	}

	async saveDeliveryOptions(deliveryOptionsSelection: {
		selectedDeliveryOption: string;
		payPalOrderId?: string;
	}): Promise<IDeliveryOptionsSaveResponse> {
		return put(
			`${checkoutApi}/express-checkout/delivery-options`,
			deliveryOptionsSelection,
		)
			.then(checkStatusVerbose)
			.then(toJson);
	}

	async initialisePayment(
		initialisePaymentRequest: IExpressCheckoutInitialisePaymentRequest,
	): Promise<IInitialisePaymentResponse> {
		return post(
			`${checkoutApi}/express-checkout/initialise-payment`,
			initialisePaymentRequest,
		)
			.then(checkStatusVerbose)
			.then(toJson);
	}

	async completeOrder(
		completeOrderRequest: IExpressCheckoutCompleteOrderRequest,
	): Promise<ICompleteOrderResponse> {
		return post(`${checkoutApi}/express-checkout/complete-order`, completeOrderRequest)
			.then(checkStatusVerbose)
			.then(toJson);
	}
}

export default ExpressCheckoutService;
